import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaBriefcase,
  FaBullhorn,
  FaCalendarAlt,
  FaCheckCircle,
  FaChalkboardTeacher,
  FaHome,
  FaHospital,
  FaLaptopCode,
  FaNewspaper,
  FaShoppingCart,
  FaUserTie
} from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import KeywordTopics from '../components/KeywordTopics.jsx';
import { setPageSEO } from '../utils/seo.js';
import { getKeywordCluster } from '../utils/keywordClusters.js';
import dashboardImage from '../assets/images/my-testimonials.png';
import './Industries.css';

const industries = [
  {
    slug: 'coaches',
    title: 'Coaches',
    description: 'Collect client success stories and transformation testimonials.',
    icon: <FaChalkboardTeacher />,
    accent: 'coral'
  },
  {
    slug: 'real-estate',
    title: 'Real Estate',
    description: 'Collect buyer, seller, and property service testimonials.',
    icon: <FaHome />,
    accent: 'blue'
  },
  {
    slug: 'healthcare',
    title: 'Doctors & Healthcare',
    description: 'Collect patient experience and healthcare service testimonials.',
    icon: <FaHospital />,
    accent: 'green'
  },
  {
    slug: 'realtors',
    title: 'Realtors',
    description: 'Collect home buyer and seller reviews and agent testimonials.',
    icon: <FaUserTie />,
    accent: 'rose'
  },
  {
    slug: 'saas',
    title: 'SaaS & Software',
    description: 'Collect customer success stories, video testimonials, and case studies.',
    icon: <FaLaptopCode />,
    accent: 'gold'
  },
  {
    slug: 'agencies',
    title: 'Agencies',
    description: 'Collect client testimonials and campaign or project success stories.',
    icon: <FaBullhorn />,
    accent: 'aqua'
  },
  {
    slug: 'consultants',
    title: 'Consultants',
    description: 'Collect client results, expertise testimonials, and recommendations.',
    icon: <FaBriefcase />,
    accent: 'violet'
  },
  {
    slug: 'ecommerce',
    title: 'Ecommerce',
    description: 'Collect product reviews, customer videos, and UGC testimonials.',
    icon: <FaShoppingCart />,
    accent: 'orange'
  },
  {
    slug: 'creators',
    title: 'Newsletters & Creators',
    description: 'Collect subscriber testimonials and audience success stories.',
    icon: <FaNewspaper />,
    accent: 'blue'
  },
  {
    slug: 'events',
    title: 'Events & Conferences',
    description: 'Collect attendee, speaker, sponsor, and event testimonials.',
    icon: <FaCalendarAlt />,
    accent: 'green'
  },
];

const reveal = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

function Industries() {
  useEffect(() => setPageSEO({
    title: 'Testimonial Software for Every Industry | Clientalio',
    description: 'Discover industry-specific ways to collect, manage, and publish authentic customer testimonials with Clientalio.',
    keywords: getKeywordCluster('industry-testimonials'),
    canonical: 'https://clientalio.com/industry-testimonials',
    ogTitle: 'Customer Proof Built for Your Industry | Clientalio',
    ogDescription: 'Turn the customer moments that matter in your industry into trusted video and text testimonials.',
    twitterTitle: 'Testimonial Software for Every Industry | Clientalio',
    twitterDescription: 'Collect and publish customer proof designed around your industry.'
  }), []);

  return (
    <main className="industries-page">
      <div className="industries-breadcrumb-wrap">
        <div className="container"><Breadcrumb currentLabel="Industry Testimonials" /></div>
      </div>

      <section className="industries-hero">
        <div className="container industries-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={reveal} className="industries-hero-copy">
            <span className="industries-eyebrow">CUSTOMER PROOF FOR EVERY MARKET</span>
            <h1>Your customers speak differently. <span>Your proof should too.</span></h1>
            <p>Clientalio helps you collect the stories buyers care about in your industry, approve every response, and publish credible proof where decisions happen.</p>
            <div className="industries-hero-actions">
              <a className="btn-primary industries-primary-button" href="/signup">Start collecting for free <FaArrowRight /></a>
              <a className="industries-text-link" href="#industry-grid">Find your industry <FaArrowRight /></a>
            </div>
            <ul className="industries-hero-proof">
              <li><FaCheckCircle /> Video, text, and audio</li>
              <li><FaCheckCircle /> Review before publishing</li>
              <li><FaCheckCircle /> No credit card required</li>
            </ul>
          </motion.div>
          <motion.div initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }} className="industries-hero-visual">
            <div className="industries-visual-label">One workspace for every customer story</div>
            <img src={dashboardImage} alt="Clientalio testimonial management dashboard" />
            <div className="industries-float-card industries-float-top"><strong>10 industries</strong><span>One simple workflow</span></div>
            <div className="industries-float-card industries-float-bottom"><FaCheckCircle /><span>Proof ready to publish</span></div>
          </motion.div>
        </div>
      </section>

      <KeywordTopics slug="industry-testimonials" />

      <section className="industries-directory" id="industry-grid">
        <div className="container">
          <div className="industries-section-heading">
            <span>BUILT AROUND YOUR BUYERS</span>
            <h2>Choose your industry</h2>
            <p>Explore practical ways to ask better questions, capture useful customer stories, and put them to work.</p>
          </div>
          <motion.div className="industries-grid" initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.1 }}>
            {industries.map((item) => (
              <motion.div key={item.slug} variants={reveal}>
                <Link to={`/industry-testimonials/${item.slug}`} className={`industry-card industry-card-${item.accent}`}>
                  <span className="industry-card-icon">{item.icon}</span>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <span className="industry-card-link">Explore solutions <FaArrowRight /></span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="industries-workflow">
        <div className="container">
          <div className="industries-section-heading">
            <span>FROM HAPPY CUSTOMER TO SALES ASSET</span>
            <h2>A simple workflow, shaped for your business</h2>
          </div>
          <div className="industries-steps">
            <article><strong>01</strong><h3>Ask the right questions</h3><p>Use prompts focused on outcomes, service quality, product value, or the proof your buyers need most.</p></article>
            <article><strong>02</strong><h3>Collect without friction</h3><p>Share one mobile-friendly link so customers can respond with video, text, or audio without creating an account.</p></article>
            <article><strong>03</strong><h3>Publish with confidence</h3><p>Review each response, organize your best stories, and embed approved proof on the pages that influence decisions.</p></article>
          </div>
        </div>
      </section>

      <section className="industries-value">
        <div className="container industries-value-grid">
          <div>
            <span className="industries-eyebrow">WHY INDUSTRY CONTEXT MATTERS</span>
            <h2>Generic praise is easy to ignore. Specific outcomes build trust.</h2>
          </div>
          <div className="industries-value-list">
            <p><FaCheckCircle /><span><strong>Speak your buyer's language.</strong> Collect stories around the risks, results, and expectations unique to your market.</span></p>
            <p><FaCheckCircle /><span><strong>Shorten the path to trust.</strong> Put relevant proof beside offers, product pages, and sales conversations.</span></p>
            <p><FaCheckCircle /><span><strong>Reuse every strong story.</strong> Turn one approved response into website proof, sales material, and social content.</span></p>
          </div>
        </div>
      </section>

      <section className="industries-cta">
        <div className="container industries-cta-inner">
          <div><span>READY WHEN YOUR CUSTOMERS ARE</span><h2>Start turning customer experiences into proof.</h2><p>Create your collection page and send your first request in under two minutes.</p></div>
          <a className="industries-cta-button" href="/signup">Start free <FaArrowRight /></a>
        </div>
      </section>
    </main>
  );
}

export default Industries;
